import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Settings, ChevronUp } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';

export function SidebarUserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    logout(); // async — clears state and redirects to /
  };

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email?.charAt(0).toUpperCase() ?? 'U';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown menu - opens upward */}
      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-2 rounded-lg border border-border bg-card py-1 shadow-lg">
          <button
            onClick={() => {
              setIsOpen(false);
              navigate('/settings');
            }}
            className="flex w-full items-center gap-3 px-3 py-2 text-sm text-foreground hover:bg-secondary"
          >
            <Settings className="h-4 w-4 text-muted-foreground" />
            <span>Settings</span>
          </button>

          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 px-3 py-2 text-sm text-red-400 hover:bg-secondary"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign out</span>
          </button>
        </div>
      )}

      {/* User badge button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-secondary/50 transition-colors"
        aria-label="User menu"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
          {getInitials(user?.name ?? undefined, user?.email)}
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="font-medium text-foreground truncate">{user?.name ?? 'User'}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
        </div>
        <ChevronUp className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
    </div>
  );
}
